import React, { Suspense, useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { CONFIG } from 'utils/config';
import { FilterName, RoutePaths } from 'types/enums';
import { useMilestonesController } from 'controllers/business/Milestones/Milestones.controller';
import CookieConsent from 'components/CookieConsent/CookieConsent';
import PoorStandingBanner from 'components/PoorStandingBanner/PoorStandingBanner';
import UserAgreements from 'components/UserAgreements/UserAgreements';
import PreviewNotification from 'components/PreviewNotification/PreviewNotification';
import FloatingActionsMenu from 'components/FloatingActionsMenu/FloatingActionsMenuConnected';
import Footer from 'components/Footer/Footer';
import AppNavigation from 'components/Navigations/AppNavigation/AppNavigation';
import Fade from 'components/Fade/Fade';
import Loader from 'components/Loader/Loader';
import Creator from 'components/Creator/Creator';
import Onboarding from 'components/Onboarding/Onboarding';
import RequireAuth from 'components/RequireAuth/RequireAuth';
import ChameleonEnabler from 'components/ChameleonEnabler/ChameleonEnabler';
import AppContentWrapper from 'components/AppContentWrapper/AppContentWrapper';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import { useErrorBoundaryContext } from 'contexts/ErrorBoundaryContext/ErrorBoundaryContext';
import Modals from 'modals';
import { trackSamplePageLoadedInPlugin } from 'App.helpers';
import {
    useDisableSpacebarScrolling,
    useResetErrorBoundaryOnLocationChange,
} from 'App.hooks';
import { useLocalePrefixes } from 'hooks/useLocalePrefixes/useLocalePrefixes';
import { lazyWithRetries } from 'utils/lazyWithRetries';
import useApp from './useApp';

// MODALS AND OVERLAYS
const PluginWelcomeDrawer = lazyWithRetries(
    () => import('./components/PluginWelcomeDrawer'),
);

// SALEBAR
const SaleBarWrapper = lazyWithRetries(
    () => import('./components/SaleBarWrapper'),
);

// PAGES
const ForYou = lazyWithRetries(() => import('pages/ForYou/ForYou'));
const Browse = lazyWithRetries(() => import('pages/Browse/Browse'));
const Charts = lazyWithRetries(() => import('pages/Charts/Charts'));
const Favorites = lazyWithRetries(() => import('pages/Favorites/Favorites'));
const PackDetails = lazyWithRetries(
    () => import('pages/PackDetails/PackDetails'),
);
const RePackDetails = lazyWithRetries(
    () => import('pages/RePackDetails/RePackDetails'),
);
const Exclusives = lazyWithRetries(() => import('pages/Exclusives/Exclusives'));
const Labels = lazyWithRetries(() => import('pages/Labels/Labels'));
const ArtistDetails = lazyWithRetries(
    () => import('pages/ArtistDetails/ArtistDetails'),
);
const Instruments = lazyWithRetries(
    () => import('pages/Instruments/Instruments'),
);
const Genres = lazyWithRetries(() => import('pages/Genres/Genres'));
const Recommendation = lazyWithRetries(
    () => import('pages/Recommendation/Recommendation'),
);
const LabelDetails = lazyWithRetries(
    () => import('pages/LabelDetails/LabelDetails'),
);
const Downloads = lazyWithRetries(() => import('pages/Downloads/Downloads'));
const CreatorLandingPage = lazyWithRetries(
    () => import('pages/CreatorLandingPage/CreatorLandingPage'),
);

const Collections = lazyWithRetries(
    () => import('pages/MyCollections/MyCollections'),
);
const CollectionDetails = lazyWithRetries(
    () => import('pages/CollectionDetails/CollectionDetails'),
);
const QuickPicks = lazyWithRetries(() => import('pages/QuickPicks/QuickPicks'));

// ERROR
const PageUnknownError = lazyWithRetries(
    () => import('error/pages/PageUnknownError/PageUnknownError'),
);

const NavigateTo = ({ url }: { url: string }) => {
    useEffect(() => {
        window.location.href = url;
    }, []);

    return null;
};

const App: React.FunctionComponent = () => {
    const {
        isDAWPlugin,
        isAuthenticated,
        loaderDisplayed,
        onFallbackLoad,
        onFallbackUnload,
        error,
    } = useApp();

    const { perform, get } = useMilestonesController();

    const { error: errorBoundaryError, setError } = useErrorBoundaryContext();

    useResetErrorBoundaryOnLocationChange(errorBoundaryError, setError);
    useDisableSpacebarScrolling();

    const localePrefixes = useLocalePrefixes();

    if (isDAWPlugin && !isAuthenticated) {
        return null;
    }

    trackSamplePageLoadedInPlugin(
        isDAWPlugin,
        get('SamplesPageLoadedInPlugin'),
        perform,
    );

    return (
        <Suspense fallback={null}>
            {/* Chameleon is configured to not run if this tag is not present */}
            {!isDAWPlugin && <ChameleonEnabler />}
            <Suspense fallback={null}>
                <SaleBarWrapper />
            </Suspense>
            <AppNavigation />
            <Fade in={!loaderDisplayed} exit={false}>
                {/* TODO: remove this 'any' */}
                {/* https://mixgenius.atlassian.net/browse/SP-6648 */}
                {({ style }: any): JSX.Element => (
                    <Suspense
                        fallback={
                            <Loader
                                onLoad={onFallbackLoad}
                                onUnload={onFallbackUnload}
                            />
                        }
                    >
                        <AppContentWrapper>
                            <>
                                {error?.variant === 'page' && (
                                    <PageUnknownError status={error?.status} />
                                )}
                                {error?.variant !== 'page' && (
                                    <Routes>
                                        {localePrefixes.map((locale) => (
                                            <Route path={locale} key={locale}>
                                                <Route
                                                    path={`${locale}`}
                                                    element={<ForYou />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.QuickPicks}`}
                                                    element={
                                                        <QuickPicks
                                                            perform={perform}
                                                        />
                                                    }
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Creator}`}
                                                    element={
                                                        <Navigate
                                                            to={`${locale}${RoutePaths.CreatorBeatMaker}`}
                                                            replace
                                                        />
                                                    }
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Creator}/:hash`}
                                                    element={
                                                        <Navigate
                                                            to={`${locale}${RoutePaths.CreatorBeatMaker}/:hash`}
                                                            replace
                                                        />
                                                    }
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.CreatorBeatMaker}`}
                                                    element={
                                                        <CreatorLandingPage />
                                                    }
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.CreatorBeatMaker}/:hash`}
                                                    element={
                                                        <CreatorLandingPage />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Plugin}`}
                                                    element={
                                                        <NavigateTo
                                                            url={
                                                                CONFIG.VITE_SAMPLES_PLUGIN_URL
                                                            }
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Browse}`}
                                                    element={<Browse />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Instruments}`}
                                                    element={<Instruments />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Instruments}/:${FilterName.Instruments}`}
                                                    element={
                                                        <Browse
                                                            withGuestMarketing
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Descriptors}/:descriptors`}
                                                    element={
                                                        <Navigate
                                                            to={`${locale}${RoutePaths.Browse}`}
                                                            replace
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Genres}`}
                                                    element={<Genres />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Genres}/:${FilterName.Genres}`}
                                                    element={
                                                        <Browse
                                                            withGuestMarketing
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Pricing}`}
                                                    element={
                                                        <NavigateTo
                                                            url={`${
                                                                CONFIG.VITE_PRICING_URL
                                                            }/samples?returnUrl=${encodeURIComponent(
                                                                CONFIG.VITE_SAMPLES_WEB_URL,
                                                            )}`}
                                                        />
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Recommendations}/:id`}
                                                    element={<Recommendation />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Packs}/:slug`}
                                                    element={<PackDetails />}
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.RePacks}/:slug`}
                                                    element={<RePackDetails />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Charts}`}
                                                    element={<Charts />}
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Charts}/:slug`}
                                                    element={<RePackDetails />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Exclusives}`}
                                                    element={<Exclusives />}
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Artists}`}
                                                    element={
                                                        <Navigate
                                                            to={`${locale}${RoutePaths.Exclusives}`}
                                                            replace
                                                        />
                                                    }
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Artists}/:slug`}
                                                    element={<ArtistDetails />}
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Labels}`}
                                                    element={<Labels />}
                                                />

                                                <Route
                                                    path={`${locale}${RoutePaths.Labels}/:slug`}
                                                    element={<LabelDetails />}
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Downloads}`}
                                                    element={
                                                        <RequireAuth>
                                                            <Downloads />
                                                        </RequireAuth>
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Favorites}`}
                                                    element={
                                                        <RequireAuth>
                                                            <Favorites />
                                                        </RequireAuth>
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Collections}`}
                                                    element={
                                                        <RequireAuth>
                                                            <Collections />
                                                        </RequireAuth>
                                                    }
                                                />
                                                <Route
                                                    path={`${locale}${RoutePaths.Collections}/:guid`}
                                                    element={
                                                        <RequireAuth>
                                                            <CollectionDetails />
                                                        </RequireAuth>
                                                    }
                                                />
                                            </Route>
                                        ))}
                                        <Route
                                            path="*"
                                            element={<PageUnknownError />}
                                        />
                                    </Routes>
                                )}
                            </>
                        </AppContentWrapper>
                        {error?.variant !== 'page' && <Footer />}
                    </Suspense>
                )}
            </Fade>
            <Suspense fallback={null}>
                <Creator />
                <FloatingActionsMenu />
                <CookieConsent />
                <PreviewNotification />
                <UserAgreements />
                <PoorStandingBanner />
                {isDAWPlugin && <PluginWelcomeDrawer />}
                <Onboarding />
                <Modals />
                <ScrollToTop />
            </Suspense>
        </Suspense>
    );
};

export default App;
